import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Undo2, Loader2, Trash2, AlertTriangle, X } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

const RefundModal = ({ order, refundable, onClose, onDone }) => {
  const [amount, setAmount] = useState(Number(refundable || 0).toFixed(2));
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("stripe");
  const [manualMode, setManualMode] = useState("cheque");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = Number(String(amount).replace(",", "."));
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    if (amt > Number(refundable) + 0.01) { toast.error(`Maximum remboursable : ${fmtEur(refundable)}`); return; }
    const isFull = amt + 0.01 >= Number(refundable);
    const warn = isFull
      ? "Remboursement TOTAL : les inscriptions de cette commande seront annulées et les places libérées. Un email sera envoyé à la famille et à l'école. Confirmer ?"
      : "Un email de confirmation sera envoyé à la famille et à l'école. Confirmer ce remboursement partiel ?";
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, {
        amount: amt, reason, method, date, manualMode: method === "manual" ? manualMode : null,
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Remboursement de ${fmtEur(data.amount)} enregistré`);
      onDone();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors du remboursement"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="refund-modal">
      <div className="bg-white rounded-2xl w-full max-w-md my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <h3 className="font-serif-display text-stone-800 flex items-center gap-2"><Undo2 size={17} className="text-deep-rose" /> Rembourser</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="refund-modal-close"><X size={17} /></button>
        </div>
        <div className="p-4 space-y-3.5">
          <div className="bg-blush-light/50 rounded-xl px-3 py-2 text-sm text-stone-700">
            Remboursable au maximum : <strong>{fmtEur(refundable)}</strong>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Montant (€) *</label>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" data-testid="refund-amount" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Méthode</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMethod("stripe")} data-testid="refund-method-stripe" className={`px-3 py-2 rounded-lg text-sm border ${method === "stripe" ? "border-deep-rose bg-blush-light text-deep-rose" : "border-stone-200 text-stone-600"}`}>Via Stripe</button>
              <button type="button" onClick={() => setMethod("manual")} data-testid="refund-method-manual" className={`px-3 py-2 rounded-lg text-sm border ${method === "manual" ? "border-deep-rose bg-blush-light text-deep-rose" : "border-stone-200 text-stone-600"}`}>Hors Stripe</button>
            </div>
            <p className="text-[11px] text-stone-500 mt-1.5">
              {method === "stripe"
                ? "Le remboursement est exécuté immédiatement sur Stripe (carte ou prélèvements SEPA déjà encaissés)."
                : "Aucun mouvement bancaire : enregistre un remboursement remis en chèque, espèces ou virement."}
            </p>
          </div>
          {method === "manual" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Moyen</label>
                <select value={manualMode} onChange={(e) => setManualMode(e.target.value)} data-testid="refund-manual-mode" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
                  <option value="cheque">Chèque</option>
                  <option value="especes">Espèces</option>
                  <option value="virement">Virement</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="refund-date" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Motif</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Désinscription, erreur de facturation…" data-testid="refund-reason" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>Un remboursement total annule les inscriptions et libère les places. Un email est envoyé à la famille et à l&apos;école.</span>
          </div>
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-deep-rose text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-50" data-testid="refund-submit">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Undo2 size={15} />} Valider le remboursement
          </button>
        </div>
      </div>
    </div>
  );
};

const RefundsSection = ({ order, onChanged }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/orders/${order.id}/refunds`);
      setData(res.data);
    } catch { /* silencieux */ }
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ligne de remboursement (saisie manuelle) ?")) return;
    try {
      await api.delete(`/admin/refunds/${id}`);
      toast.success("Ligne supprimée");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const refunds = (data?.refunds || []).filter((r) => r.status !== "canceled");

  return (
    <section className="mt-6" data-testid="refunds-section">
      <h3 className="font-serif-display text-lg text-stone-800 mb-3 flex items-center gap-2"><Undo2 size={16} className="text-deep-rose" /> Remboursements</h3>
      <div className="bg-white border border-stone-200 rounded-2xl p-4 text-sm space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="text-stone-600">Encaissé : <strong className="text-stone-800">{fmtEur(data?.totalPaid)}</strong></span>
          <span className="text-stone-600">Remboursé : <strong className="text-rose-700" data-testid="refund-total">{fmtEur(data?.totalRefunded)}</strong></span>
          <span className="text-stone-600">Net encaissé : <strong className="text-emerald-700" data-testid="refund-net">{fmtEur((data?.totalPaid || 0) - (data?.totalRefunded || 0))}</strong></span>
        </div>

        {refunds.length > 0 && (
          <div className="divide-y divide-stone-100 border-t border-stone-100 pt-1">
            {refunds.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2" data-testid={`refund-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-stone-800">{fmtEur(r.amount)} <span className="text-xs text-stone-400">· {r.source === "stripe" ? "Stripe" : `Hors Stripe${r.manualMode ? ` (${r.manualMode})` : ""}`}</span></div>
                  <div className="text-xs text-stone-500">{fmtDate(r.refundedAt)}{r.reason ? ` — ${r.reason}` : ""}</div>
                </div>
                {r.source !== "stripe" && (
                  <button onClick={() => remove(r.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" data-testid={`refund-delete-${r.id}`}><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setOpen(true)}
          disabled={!data || (data.refundableAmount || 0) <= 0}
          data-testid="refund-open-btn"
          className="inline-flex items-center gap-1.5 bg-deep-rose hover:brightness-110 text-white px-4 py-2 rounded-full text-sm disabled:opacity-40"
        >
          <Undo2 size={14} /> Rembourser
        </button>
        {data && (data.refundableAmount || 0) <= 0 && (
          <p className="text-xs text-stone-500">Rien à rembourser : aucun encaissement restant sur cette commande.</p>
        )}
      </div>

      {open && (
        <RefundModal
          order={order}
          refundable={data?.refundableAmount || 0}
          onClose={() => setOpen(false)}
          onDone={() => { load(); onChanged?.(); }}
        />
      )}
    </section>
  );
};

export default RefundsSection;
